import { FC, PropsWithChildren, useEffect, useState } from 'react';

import { Box, Paper, SxProps, TableContainer, Theme } from '@mui/material';
import useSettingsStore from 'app/store/useSettingsStore';
import Scrollbars from 'react-custom-scrollbars-2';

import useScrollPercentage from 'shared/hooks/useScrollPercentage';
import { TablePagination } from 'shared/ui/pagination';

import CircularProgressCenter from '../CircularProgressCenter';
import { CustomScrollbar } from '../custom-scrollbar';

interface IProps extends PropsWithChildren {
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  total: number;
  sx?: SxProps<Theme>;
  isDataLoading?: boolean;
}

export const MethodTableLayout: FC<IProps> = ({
  children,
  pageSize,
  setPageSize,
  total,
  sx,
  isDataLoading
}) => {
  const { testMode } = useSettingsStore();
  const { handleScroll, scrollbarRef, contentRef, ...customScrollProps } = useScrollPercentage();
  const [showScroll, setShowScroll] = useState(false);
  useEffect(() => {
    if (total) {
      setShowScroll(true);
    }
  }, [total]);
  const { darkTheme } = useSettingsStore();

  return (
    <Box
      sx={{
        ...sx,
        gridRow: '3/4',
        gridColumn: '2',
        '> .MuiGrid-root': {
          border: '1px solid',
          borderColor: darkTheme ? 'var(--mui-palette-divider)' : 'transparent'
        }
      }}>
      {isDataLoading ? (
        <CircularProgressCenter top='50%' />
      ) : (
        <>
          <Box
            sx={{
              overflow: 'auto',
              '&.MuiBox-root': {
                maxWidth: '100%',
                width: '100%',
                height: 'calc(100% - 70px)'
              }
            }}>
            <TableContainer
              sx={{
                height: 1,
                boxShadow: 'none',
                position: 'relative',
                display: 'flex',
                justifyContent: 'space-between',
                overflow: 'hidden',
                backgroundImage: 'none',
                '& > div:first-child > div': {
                  backgroundColor: 'var(--mui-palette-background-paper)',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  gap: '1px'
                },
                '&:after': {
                  content: testMode ? undefined : `''`,
                  width: '1px',
                  height: '97%',
                  position: 'absolute',
                  top: '1.5%',
                  left: '49.75%',
                  transform: 'translate(-50%, 0%)',
                  zIndex: '100',
                  background: 'var(--mui-palette-background-default)'
                },
                '::-webkit-scrollbar': {
                  display: 'none',
                  width: '0',
                  background: 'transparent',
                  scrollbarColor: 'transparent transparent'
                }
              }}
              component={Paper}>
              <Scrollbars
                ref={scrollbarRef}
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  height: 'calc(100% - 12px)',
                  background: 'var(--mui-palette-background-paper)',
                  scrollbarColor: 'transparent transparent',

                  border: '1px solid',
                  borderColor: darkTheme ? 'var(--mui-palette-divider)' : 'transparent'
                }}
                renderTrackVertical={undefined}
                renderThumbVertical={undefined}
                onScroll={handleScroll}>
                <Box
                  ref={contentRef}
                  key={isDataLoading?.toString()}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    width: '100%'
                  }}>
                  {children}
                </Box>
              </Scrollbars>
              {showScroll && <CustomScrollbar customScrollProps={customScrollProps} />}
            </TableContainer>
          </Box>
          <TablePagination pageSize={pageSize} setPageSize={setPageSize} total={total} />
        </>
      )}
    </Box>
  );
};
